import { 
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  ChangeEventHandler,
} from 'react';

import Button from 'components/Button/Button';
import ValidationService from 'services/ValidationService/ValidationService';
import CareerService from 'services/CareerService/CareerService';
import { CountryCodeType } from 'types/types';
import useLocalised from 'customHooks/useLocalised/useLocalised';
import TextField from 'components/TextField/TextField';
import Select, { SelectOptionType } from 'components/Select/Select';
import {
  name_is_required,
  apply,
  name,
  phone_number,
  phone_number_required,
  email,
  country_code,
  submit,
  email_address_required,
  any_additional_comments,
  thank_you,
  job_application_sent_success_message,
  experienced_travel_agent,
  new_travel_agent,
} from 'data/translationKeys';
import RadioButton, { RadioOptionType } from 'components/RadioButton/RadioButton';

interface ICareerForm {
  countryCodes: CountryCodeType[],
}

export type CareersFormsStateType = {
  isLoading: boolean,
  success: boolean,
  name: string,
  email: string,
  phoneNumber: string,
  country: string,
  comment: string,
  experince: string,
  errors: {
    name: boolean,
    email: boolean,
    phoneNumber: boolean,
    country: boolean,
  },
};

const initialCareersState: CareersFormsStateType = {
  isLoading: false,
  success: false,
  name: '',
  email: '',
  phoneNumber: '',
  country: '',
  comment: '',
  experince: "experienced",
  errors: {
    name: false,
    email: false,
    phoneNumber: false,
    country: false,
  },
};

const ORDERED_FIELD_KEYS = [
  "name",
  "phoneNumber",
  "email",
];

const CareerForm: FC<ICareerForm> = ({
  countryCodes,
}) => {
  const [state, setState] = useState<CareersFormsStateType>(initialCareersState);

  const userInputRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const giveRef = useCallback<(instance: HTMLDivElement | null, inputName: string) => void>((instance, inputName) => {
    userInputRefs.current = {
      ...userInputRefs.current,
      [inputName]: instance,
    };
  }, []);

  const handleScrollToError = useCallback((errorObject: {[key: string]: boolean}) => {
    let scrollHandled = false;
    let index = 0;
    while (index < ORDERED_FIELD_KEYS.length && scrollHandled === false) {
      const key = ORDERED_FIELD_KEYS[index];
      if (errorObject[key] && userInputRefs.current[key]) {
        userInputRefs.current[key]?.scrollIntoView({ behavior: 'smooth' });
        scrollHandled = true;
      }
      index += 1;
    }
  }, []);

  const formIsValid = useCallback<() => boolean>(() => {
    let isValid = true;
    let errorObject = {};

    if (!ValidationService.isValidEmail(state.email)) {
      isValid = false;
      errorObject = {
        ...errorObject,
        email: true,
      };
    }

    if (!ValidationService.isValidName(state.name)) {
      isValid = false;
      errorObject = {
        ...errorObject,
        name: true,
      };
    }

    if (!ValidationService.isValidPhoneNumber(state.phoneNumber)) {
      isValid = false;
      errorObject = {
        ...errorObject,
        phoneNumber: true,
      };
    }

    if (!ValidationService.isValidCountryCode(state.country)) {
      isValid = false;
      errorObject = {
        ...errorObject,
        countryCode: true,
      };
    }

    if (!isValid) {
      handleScrollToError(errorObject);
      setState(state => ({
        ...state,
        errors: {
          ...state.errors,
          ...errorObject,
        },
      }));
    }

    return isValid;
  }, [handleScrollToError, state.country, state.email, state.name, state.phoneNumber]);

  const handleTextChange = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    const key = e.target.name as keyof CareersFormsStateType;
    setState(state => ({
      ...state,
      errors: {
        ...initialCareersState.errors
      },
      [key]: e.target.value,
    }));
  }, []);
  
  const handleSelectChange = useCallback((name: string, value: string) => {
    const key = name as keyof CareersFormsStateType;
    setState(state => ({
      ...state,
      errors: {
        ...initialCareersState.errors,
      },
      [key]: value,
    }));
  }, []);

  const handleRadioButtonChange = useCallback((name: string, value: string) => {
    const key = name as keyof CareersFormsStateType;
    setState(state => ({
      ...state,
      [key]: value,
    }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!formIsValid()) {
      return;
    }
    setState(state => ({
      ...state,
      isLoading: true,
    }));
    CareerService.sendJobApplication({
      name: state.name,
      email: state.email,
      phone_number: state.phoneNumber,
      country: state.country,
      comment: state.comment,
      experince: state.experince,
    }).then(() => {
      setState(state => ({
        ...state,
        success: true,
        isLoading: false,
      }));
    }).catch(() => {
      setState(state => ({
        ...state,
        isLoading: false,
      }));
    });
  }, [formIsValid, state.comment, state.country, state.email, state.experince, state.name, state.phoneNumber]);

  const experienced_travel_agent_text = useLocalised(experienced_travel_agent);
  const new_travel_agent_text = useLocalised(new_travel_agent);

  const radioButtonOptions = useMemo<RadioOptionType[]>(() => ([{
    value: "experienced",
    label: experienced_travel_agent_text,
  }, {
    value: "new",
    label: new_travel_agent_text,
  }]), [experienced_travel_agent_text, new_travel_agent_text]);

  const countryCodeOptions = useMemo<SelectOptionType[]>(() => {
    return countryCodes.map(countryCodes => ({
      value: countryCodes.id.toString(),
      name: countryCodes.name,
    }));
  }, [countryCodes]);

  useEffect(() => {
    if (state.country || countryCodes.length === 0) {
      return;
    }
    const GBCode = countryCodes.find(({ name }) => name === "United Kingdom");
    if (GBCode !== undefined) {
      setState(state => ({
        ...state,
        country: `${GBCode.name}`,
      }));
      return;
    }
    setState(state => ({
      ...state,
      countryCode: `${countryCodes[0].id}`,
    }));
  }, [countryCodes, setState, state.country]);

  const country_code_text = useLocalised(country_code);
  const email_text = useLocalised(email);
  const name_is_required_text = useLocalised(name_is_required);
  const name_text = useLocalised(name);
  const submit_text = useLocalised(submit);
  const phone_number_text = useLocalised(phone_number);
  const email_address_required_text = useLocalised(email_address_required);
  const phone_number_required_text = useLocalised(phone_number_required);
  const any_additional_comments_text = useLocalised(any_additional_comments);
  const apply_text = useLocalised(apply);
  const thank_you_text = useLocalised(thank_you);
  const job_application_sent_success_message_text = useLocalised(job_application_sent_success_message);

  return (
    <div className="career-form-container">
      {state.success && (
        <>
          <div className="form-title">
            {thank_you_text}
          </div>
          <div className="form-success-message">
            {job_application_sent_success_message_text}
          </div>
        </>
      )}
      {!state.success && (
        <>
          <div className="form-title">
            {apply_text}
          </div>
          <TextField
            ref={(instance) => giveRef(instance as HTMLDivElement, "name")}
            label={name_text}
            type="text"
            name="name"
            value={state.name}
            onChange={handleTextChange}
            error={state.errors.name}
            helperText={state.errors.name && name_is_required_text}
            hasErrorMessage
          />
          <TextField
            ref={(instance) => giveRef(instance as HTMLDivElement, "phoneNumber")}
            label={phone_number_text}
            type="text"
            name="phoneNumber"
            value={state.phoneNumber}
            onChange={handleTextChange}
            error={state.errors.phoneNumber}
            helperText={state.errors.phoneNumber && phone_number_required_text}
            hasErrorMessage
          />
          <TextField
            ref={(instance) => giveRef(instance as HTMLDivElement, "email")}          
            label={email_text}
            type="email"
            name="email"
            value={state.email}
            onChange={handleTextChange}
            error={state.errors.email}
            helperText={state.errors.email && email_address_required_text}
            hasErrorMessage
          />
          <Select
            label={country_code_text}
            value={state.country}
            name="country"
            onChange={handleSelectChange}
            options={countryCodeOptions}
          />
          <RadioButton 
            name="experince"
            options={radioButtonOptions} 
            value={state.experince}
            onRadioButtonClick={handleRadioButtonChange}
          />
          <TextField
            label={any_additional_comments_text}
            type="text"
            name="comment"
            value={state.comment}
            onChange={handleTextChange}
            multiline
            rows={4}
          />
          <div className='form-submit'>
            <Button
              onClick={handleSubmit}
              title={submit_text}
              className={"form-submit_button"}
              isLoading={state.isLoading}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default CareerForm;
